import {
  alpha,
  createTheme,
  darken,
  PaletteOptions,
  Theme,
  ThemeOptions,
  ThemeProvider,
  useMediaQuery,
} from '@mui/material';
import Color from 'color';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers';
// Types only: adds the picker components' keys to the theme's `components`.
import type {} from '@mui/x-date-pickers/themeAugmentation';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import CssBaseline from '@mui/material/CssBaseline';

import { setThemeColorBase } from './hooks/use-theme-color/use-theme-color';
import {
  APP_HEADER_HEIGHT,
  APP_HEADER_HEIGHT_COMPACT,
  PHONE_LANDSCAPE_MAX_WIDTH,
  PHONE_LANDSCAPE_QUERY,
} from './theme-constants';

declare module '@mui/material/styles' {
  interface Palette {
    tertiary?: Palette['primary'];
    backdrop?: { overlay: string };

    calendar1: Palette['primary'];
    calendar2: Palette['primary'];
    calendar3: Palette['primary'];

    statusRequested: Palette['primary'];
    statusScheduled: Palette['primary'];
    statusInProgress: Palette['primary'];
    statusCompleted: Palette['primary'];

    jobs: Palette['primary'];
    invoices: Palette['primary'];
    animalHeads: Palette['primary'];
    users: Palette['primary'];
    waitlist: Palette['primary'];
  }
  interface PaletteOptions {
    tertiary?: PaletteOptions['primary'];
    calendar1?: PaletteOptions['primary'];
    calendar2?: PaletteOptions['primary'];
    calendar3?: PaletteOptions['primary'];
    backdrop?: { overlay: string };

    statusRequested?: PaletteOptions['primary'];
    statusScheduled?: PaletteOptions['primary'];
    statusInProgress?: PaletteOptions['primary'];
    statusCompleted?: PaletteOptions['primary'];

    jobs?: PaletteOptions['primary'];
    invoices?: PaletteOptions['primary'];
    animalHeads?: PaletteOptions['primary'];
    users?: PaletteOptions['primary'];
    waitlist?: PaletteOptions['primary'];
  }
}

export * from './theme-constants';

/**
 * Where `sm` and `md` sit on a landscape phone: above the widest phone, so the
 * whole app keeps its phone layout in both orientations instead of a desktop
 * one squeezed into 400px of height. Still ascending and below `lg`.
 */
const PHONE_LANDSCAPE_BREAKPOINTS = {
  sm: PHONE_LANDSCAPE_MAX_WIDTH + 1,
  md: PHONE_LANDSCAPE_MAX_WIDTH + 51,
};

const calendarColors = ['#4b85ff', '#ff9b09', '#ff5670'].reduce(
  (acc, color, idx) => ({
    ...acc,
    [`calendar${idx + 1}`]: {
      main: color,
      light: alpha(color, 0.5),
      dark: alpha(color, 0.9),
      contrastText: '#fff',
    },
  }),
  {} as Partial<PaletteOptions>,
);

/**
 * Keeps the mobile date/time pickers out of the full-bleed dialog rule.
 *
 * On a touch device `DatePicker` & co. render their `Mobile*` variant, which
 * shows the calendar in a plain MUI Dialog — same `.MuiDialog-paper`, default
 * `maxWidth="sm"`, no class of its own — so the phone-wide rule in
 * `MuiCssBaseline` stretched it to fill the screen. Stamping the rule's own
 * opt-out class on the picker's paper is the one hook that doesn't depend on
 * `:has()`; MUI merges `slotProps` per slot, so a call site that passes its own
 * (a text field's, say) keeps this.
 */
const pickerDialogSlotProps = {
  slotProps: { mobilePaper: { className: 'fs-dialog-compact' } },
};
const pickerDialogDefaults: ThemeOptions['components'] = {
  MuiMobileDatePicker: { defaultProps: pickerDialogSlotProps },
  MuiMobileTimePicker: { defaultProps: pickerDialogSlotProps },
  MuiMobileDateTimePicker: { defaultProps: pickerDialogSlotProps },
};

/**
 * Mobile gutters for the data grids.
 *
 * Same trade as the `MuiTableCell` override: DataGrid gives every cell and
 * header `padding: 0 10px`, so a nine-column editing grid spends 180px on
 * gutters before a value draws. On a phone that is half the screen, and these
 * grids scroll horizontally, so the cost is paid in swipes.
 *
 * This has to be a `MuiDataGrid` style override rather than global CSS. The
 * grid's own padding rule is `.MuiDataGrid-root .MuiDataGrid-cell` — two
 * classes — so a bare `.MuiDataGrid-cell` rule in the baseline loses to it and
 * does nothing (which is what shipped first). An override is compiled into the
 * grid root's own class: same specificity as the rule it replaces, after it in
 * the sheet, and still before a call site's `sx`, so a grid that sets its own
 * cell padding keeps it.
 *
 * The `:where(:not(...))` exclusions are the cells the grid deliberately gives
 * different padding — the checkbox column (0), the filler cell (0), and a cell
 * in edit mode (1px, so the editor fills it). `:where` keeps them from adding
 * specificity. Vertical padding is untouched: row height is a touch target,
 * and DataGrid derives it from its own density setting.
 *
 * Typed loosely because the `MuiDataGrid` theme key only exists once the
 * grid's `themeAugmentation` is imported, and this package does not depend on
 * the grid — only web does.
 */
const dataGridMobileGutters = {
  MuiDataGrid: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        [theme.breakpoints.down('sm')]: {
          [[
            '& .MuiDataGrid-columnHeader:where(:not(.MuiDataGrid-columnHeaderCheckbox))',
            '& .MuiDataGrid-cell:where(:not(.MuiDataGrid-cellCheckbox):not(.MuiDataGrid-cellEmpty):not(.MuiDataGrid-cell--editing))',
          ].join(', ')]: {
            paddingLeft: 6,
            paddingRight: 6,
          },
        },
      }),
    },
  },
} as unknown as ThemeOptions['components'];

const themeDefaults: Omit<ThemeOptions, 'palette'> = {
  typography: {
    fontFamily: "'Figtree', Arial, sans-serif",
    button: { letterSpacing: '0.46px' },
    allVariants: { color: 'text.primary' },
    h1: { fontFamily: "'Poppins', serif", fontSize: '4rem' },
    h2: { fontFamily: "'Poppins', serif", fontSize: '3rem' },
    h3: { fontFamily: "'Poppins', serif", fontSize: '2rem' },
    h4: { fontFamily: "'Poppins', serif", fontSize: '1.5rem' },
    h5: { fontFamily: "'Poppins', serif", fontSize: '1.3rem' },
    h6: { fontSize: '1.2rem', fontWeight: 700 },
    overline: { fontWeight: 400, letterSpacing: '0.1em' },
  },
  components: {
    // Add print-specific overrides
    MuiCssBaseline: {
      styleOverrides: (theme) => `
        @media print {
          body {
            backgroundColor: #ffffff !important;
            color: #2A2A2A !important;
          }
          * {
            backgroundColor: transparent !important;
            color: #2A2A2A !important;
            box-shadow: none !important;
          }
          .MuiButton-root, .MuiChip-root {
            backgroundColor: #537f68 !important;
            color: #ffffff !important;
          }
          .MuiContainer-root {
            margin-left: 0;
            margin-right: 0;
            max-width: unset !important;
          }
          .MuiAppBar-root {
            display: none !important;
          }
        }

        /* Custom scrollbar styles for all scrollable elements */
        * {
          /* Firefox */
          scrollbar-width: thin;
          scrollbar-color: ${
            theme.palette.mode === 'dark'
              ? 'rgba(107, 155, 132, 0.2) transparent'
              : 'rgba(83, 127, 104, 0.1) transparent'
          };
        }

        /* Chrome, Safari, Edge */
        *::-webkit-scrollbar {
          width: 4px;
          height: 4px;
        }

        *::-webkit-scrollbar-track {
          background: transparent;
        }

        *::-webkit-scrollbar-thumb {
          background: ${
            theme.palette.mode === 'dark'
              ? 'rgba(107, 155, 132, 0.2)'
              : 'rgba(83, 127, 104, 0.1)'
          };
          border-radius: 4px;
          transition: background 0.2s ease;
        }

        *::-webkit-scrollbar-thumb:hover {
          background: ${
            theme.palette.mode === 'dark'
              ? 'rgba(107, 155, 132, 0.4)'
              : 'rgba(83, 127, 104, 0.2)'
          };
        }

        *::-webkit-scrollbar-corner {
          background: transparent;
        }

        /* Full-bleed dialogs on phones.

           MUI's fullScreen prop is per-dialog, and this app has ~76 raw
           Dialogs that never set it — each one a centred card with 32px of
           margin and a 64px max-height cap, holding forms, grids and pickers.
           Rather than 76 edits, the same geometry is applied here.

           Two deliberate exclusions:
           - paperWidthXs: asking for maxWidth="xs" is a statement that the
             dialog is meant to be small. Confirms use it, and a one-sentence
             confirm blown up to fill a phone reads as a page, not a prompt.
           - fs-dialog-compact: the escape hatch for anything that has already
             decided its own mobile size. ModalRenderer stamps it on every
             modal it chose NOT to make fullscreen, so the provider keeps
             authority over its own dialogs (see CENTERED_ON_MOBILE_TYPES).
             The date/time pickers get it too, through their theme defaults
             below (see pickerDialogDefaults): their mobile variant is a MUI
             Dialog with nothing to tell it apart in CSS, and a calendar
             stretched to a full-height sheet leaves its buttons stranded
             halfway down the screen.

           This rule beats a size set through PaperProps.sx, and that is on
           purpose. Its selector carries two :not() classes on top of the
           paper class, where sx (like MUI's own paper styles) is a single
           class, and it has to outrank MUI's to work at all — there is no
           specificity that beats one and yields to the other. The dialogs
           that size themselves that way ask for "80vh" or "90vh" of a
           desktop window, which on a phone is exactly the letterboxed card
           this rule exists to remove. A dialog that really has designed its
           own phone geometry opts out with fs-dialog-compact on its paper.

           NOTE: no backticks in this comment — it lives inside a template
           literal, so one would end the string. */
        @media (max-width: 600px), ${PHONE_LANDSCAPE_QUERY} {
          .MuiDialog-paper:not(.MuiDialog-paperWidthXs):not(.fs-dialog-compact) {
            margin: 0;
            width: 100%;
            max-width: 100%;
            height: 100%;
            max-height: 100%;
            border-radius: 0;
          }
        }

        /* A full-screen dialog runs edge to edge, which in landscape puts its
           contents under the notch. Inset the contents, not the paper, so the
           dialog colour still fills the notch area. Both sides: which one the
           notch is on depends on rotation direction. Zero in portrait. */
        @media ${PHONE_LANDSCAPE_QUERY} {
          .MuiDialog-paperFullScreen,
          .MuiDialog-paper:not(.MuiDialog-paperWidthXs):not(.fs-dialog-compact) {
            padding-left: env(safe-area-inset-left, 0px);
            padding-right: env(safe-area-inset-right, 0px);
          }

          /* MUI caps a dialog at the sm / md breakpoint VALUE, and both are
             lifted above the screen on a landscape phone. The dialogs kept
             as centred cards hold the widths they have everywhere else. */
          .MuiDialog-paperWidthSm.fs-dialog-compact {
            max-width: 600px;
          }
          .MuiDialog-paperWidthMd.fs-dialog-compact {
            max-width: 900px;
          }
        }

        /* SUP-407 — stop iOS zooming the whole app in.

           WebKit (iOS Safari AND iOS Chrome, which is WebKit underneath) zooms
           the page in whenever a focused form control's computed font-size is
           below 16px, and it never zooms back out. In an SPA there is no page
           load to reset the scale either, so one tap into one field leaves
           EVERY subsequent screen sitting at that zoom — which is why this
           reads as "every screen slightly zooms in for no reason".

           Every ordinary MUI field in this app is one pixel under that
           threshold: bootstrap/scss/root emits a :root font-size of
           var(--bs-root-font-size), fed by our $font-size-root: 15px
           (variables.scss), so MUI's 1rem body1 input resolves to 15px, not
           16px. Measured on the login field: 15px. Raising $font-size-root
           would rescale the entire app by 6.25%, so floor the controls
           instead — and only on touch-primary devices, leaving desktop's dense
           size="small" layouts exactly as they are.

           The !important is deliberate: the values being overridden come from
           per-component sx classes, which otherwise win on specificity.

           NOT fixed with maximum-scale=1 / user-scalable=no on the viewport
           tag — that kills pinch-to-zoom, which people need.

           Scoped to phone-sized viewports, not to every coarse pointer. The
           focus-zoom is an iPhone viewport behaviour; iPad and Android don't
           do it, and on a tablet this rule was pure cost — the dense inline
           table editors (editable-data-list-view, animal-heads-list, and the
           other 13px cells) pin their row height with a
           height:24px !important, so growing the glyphs ~23% inside a locked box clipped
           descenders and truncated the values. The two-arm query keeps phones
           in both orientations (landscape phones are wider than 600px but
           never taller than ~500px) and leaves every tablet out.

           MuiPickers* is listed because @mui/x-date-pickers v8 defaults
           enableAccessibleFieldDOMStructure to true: the digit sections are
           contenteditable spans but the slash and colon separators are not, and
           the container carries MuiPickersInputBase-* rather than
           MuiInputBase-input. Without these selectors a date field renders
           16px digits next to 15px separators, and the separators flip size
           as the selection changes while typing. */
        @media (hover: none) and (pointer: coarse) and (max-width: 600px),
          ${PHONE_LANDSCAPE_QUERY} {
          input:not([type='checkbox']):not([type='radio']):not([type='range']):not([type='color']):not([type='button']):not([type='submit']):not([type='reset']),
          select,
          textarea,
          [contenteditable]:not([contenteditable='false']),
          .MuiInputBase-input,
          .MuiSelect-select,
          .MuiNativeSelect-select,
          .MuiPickersInputBase-root,
          .MuiPickersSectionList-root {
            font-size: 16px !important;
          }
        }
      `,
    },
    MuiTextField: { defaultProps: { fullWidth: true } },
    ...pickerDialogDefaults,
    ...dataGridMobileGutters,
    MuiPaper: { styleOverrides: { root: { color: 'text.disabled' } } },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: 'none',
          fontWeight: 400,
          // MUI's 16px side padding on the first tab compounds with whatever
          // gutter the container already spent, which is how a tab strip inside
          // a dialog ends up starting 40px into a 390px screen. Trimmed to 10px
          // below `sm`; `minWidth` is left alone so tabs stay tappable.
          [theme.breakpoints.down('sm')]: {
            paddingLeft: theme.spacing(1.25),
            paddingRight: theme.spacing(1.25),
          },
        }),
      },
    },
    MuiButton: { styleOverrides: { root: { textTransform: 'none' } } },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { borderRadius: '10px' } },
    },
    MuiContainer: {
      styleOverrides: {
        // Halve the page gutter on phones: MUI's own default is 16px a side,
        // which costs 32px of a ~390px screen before a page has drawn anything
        // — and it is only the outermost of three nested paddings, since most
        // pages then put a Card (another ~15px a side) around the content.
        //
        // 8px rather than 0: content still clears the screen edge, so a Card
        // reads as a card and a table's first column doesn't touch the bezel.
        //
        // Scoped below `sm` only. Above it the Container's own `up('sm')` rule
        // still wins (both are media queries and ours no longer matches), so
        // desktop keeps its 24px untouched.
        //
        // The `disableGutters` guard matters: Container applies its padding as
        // `...(!ownerState.disableGutters && {...})` in the base styles, and
        // theme styleOverrides are appended AFTER those — so without the guard
        // this would hand padding back to every Container that asked for none.
        //
        // Pages that already zero their own xs gutter by hand (processor
        // settings, cutsheet v2, questionnaire responses) pass `px` through
        // `sx`, which outranks styleOverrides, and are unaffected.
        root: ({ theme, ownerState }) =>
          ownerState.disableGutters
            ? {}
            : {
                [theme.breakpoints.down('sm')]: {
                  paddingLeft: theme.spacing(1),
                  paddingRight: theme.spacing(1),
                },
              },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        action: { marginLeft: 8 },
        // Below `sm`, close the 32px trough MUI leaves between a card's title
        // and its first row (CardHeader's 16px bottom + CardContent's 16px
        // top). On a phone that gap is pure scroll: a six-row profile card
        // spends a third of its height on chrome. Desktop keeps the 16px.
        root: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: {
            paddingBottom: theme.spacing(1),
          },
        }),
      },
    },
    MuiCardContent: {
      styleOverrides: {
        // Pairs with the CardHeader rule above. `last-child` is MUI's own
        // 24px bottom pad on the final CardContent, which on a phone reads as
        // an empty row under the content — matched to the 16px sides instead.
        root: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: {
            paddingTop: theme.spacing(1),
            '&:last-child': { paddingBottom: theme.spacing(2) },
          },
        }),
      },
    },
    MuiMenu: {
      // `invisible`, not a transparent background. The two look identical,
      // but only the flag carries the class the backdrop rule below keys on
      // to sit at `top: 0` — and a menu opened from the app header has the
      // same dismiss problem a popover does otherwise.
      defaultProps: { BackdropProps: { invisible: true } },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: 'backdrop.overlay',
          // Start below the app header. Safari tints the status bar from the
          // topmost element's OWN background-colour — not from anything painted
          // over it — so dimming the AppBar leaves the strip above it bright and
          // the seam shows. Leaving the header undimmed keeps header and strip
          // matching. 0 where there's no top bar (desktop left nav).
          top: 'var(--app-header-height, 0px)',
          // `bottom: 0`, deliberately — NOT an overshoot, and not a computed
          // height. Measured on device: with `bottom: -200` the rect genuinely
          // spanned past the screen (954px on a 754px screen) and still didn't
          // paint behind Safari's toolbar, so extending past the viewport gets
          // the overhang clipped and takes the behind-the-bar part with it.
          //
          // A bottom-anchored Drawer paper — the one surface that has always
          // painted correctly down there — uses plain `bottom: 0`. Match it.
          bottom: 0,
          // ...but only where there is dimming to seam. An INVISIBLE backdrop
          // paints nothing, so the Safari argument above doesn't apply to it —
          // all it does is catch the click that dismisses the thing it belongs
          // to. Offsetting it left the app header uncovered, so a Popover
          // anchored to an icon IN the header (the notification bell) could not
          // be dismissed by tapping that icon again: the tap landed on the
          // header, not on the backdrop, and nothing happened.
          '&.MuiBackdrop-invisible': { top: 0 },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: { root: { '&:before': { display: 'none' } } },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          width: '40px',
          height: '40px',
          backgroundColor: 'primary.light',
          color: 'primary.contrastText',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: { root: { backgroundColor: 'background.input' } },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          background: 'none !important',
          // Halve the cell gutter on phones.
          //
          // MUI's `size="small"` cell — what every list table in this app uses
          // — is `padding: '6px 16px'`, so 32px of every column is padding. On
          // a 6-column table that is 192px of a ~336px content area: more than
          // half the screen spent on whitespace, which is what pushes columns
          // out behind `minimumBreakpoint` earlier than they need to go.
          //
          // Vertical padding is deliberately untouched — row height is a
          // touch-target concern, and several dense inline editors pin it.
          //
          // `checkbox` and `none` are excluded: those variants exist precisely
          // to control their own horizontal padding (a 4px selection gutter, or
          // zero for a cell that draws its own), and overriding them would undo
          // the selection column's alignment.
          ...(ownerState.padding !== 'checkbox' &&
            ownerState.padding !== 'none' && {
              [theme.breakpoints.down('sm')]: {
                paddingLeft: theme.spacing(1),
                paddingRight: theme.spacing(1),
              },
            }),
        }),
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        selectLabel: { margin: 0 },
        displayedRows: { margin: 0 },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { backgroundColor: 'background.paper' },
        // `scrollButtons="auto"` renders BOTH arrows and merely disables the
        // one that can't scroll — so a strip sitting at the left edge starts
        // ~40px in, behind an invisible button, and the first tab never
        // touches the gutter. Collapse the disabled one to nothing instead,
        // and animate it so the arrow appears as you scroll rather than
        // snapping the row sideways.
        //
        // Width, not `display: none`: a display swap re-lays-out the strip in
        // one frame and the tabs jump under the finger mid-scroll.
        scrollButtons: {
          transition: 'width 150ms ease, opacity 150ms ease',
          overflow: 'hidden',
          '&.Mui-disabled': {
            width: 0,
            minWidth: 0,
            opacity: 0,
          },
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: { root: { color: 'primary.main' } },
    },
    MuiDialogTitle: { defaultProps: { variant: 'h5' } },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: 'underline',
          fontWeight: 'bold',
          color: 'primary.main',
        },
      },
    },
    MuiSkeleton: {
      defaultProps: { animation: 'wave' },
      styleOverrides: { root: { '&::after': { animationDuration: '1.5s' } } },
    },
    MuiDivider: {
      styleOverrides: { root: { opacity: 1, borderColor: 'divider' } },
    },
    MuiAlert: {
      defaultProps: { icon: false, variant: 'filled' },
      styleOverrides: {
        root: ({ theme }) => ({
          background:
            theme.palette.mode === 'dark'
              ? `linear-gradient(0deg, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3))`
              : `linear-gradient(0deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.3))`,
        }),
        filledError: ({ theme }) => ({
          backgroundColor:
            theme.palette.mode === 'dark'
              ? theme.palette.error.dark
              : theme.palette.error.light,
          color: theme.palette.text.primary,
        }),
        // Unlike its siblings, this can't use `info.dark` in dark mode.
        // `info.main` is a pale blue there, so its dark shade lands around
        // 2:1 against `text.primary` — while error.dark and success.dark,
        // derived from already-deep colors, sit at 6:1 and 7.6:1. `info.dark`
        // also does duty as a TEXT color elsewhere in the app, where it needs
        // to stay light on a dark surface, so it can't simply be deepened.
        // Deriving the background here keeps it tracking `info.main` and lands
        // at ~6.4:1, in line with the other filled alerts.
        filledInfo: ({ theme }) => ({
          backgroundColor:
            theme.palette.mode === 'dark'
              ? darken(theme.palette.info.main, 0.6)
              : theme.palette.info.light,
          color: theme.palette.text.primary,
        }),
        filledSuccess: ({ theme }) => ({
          backgroundColor:
            theme.palette.mode === 'dark'
              ? theme.palette.success.dark
              : theme.palette.success.light,
          color: theme.palette.text.primary,
        }),
        filledWarning: ({ theme }) => ({
          backgroundColor:
            theme.palette.mode === 'dark'
              ? theme.palette.warning.dark
              : theme.palette.warning.light,
          color: theme.palette.text.primary,
        }),
        // `icon` below is taken out of flow so that alerts without one (the
        // default) get no leading gutter. That means an alert that DOES pass an
        // icon has to reserve the space here, or the icon renders on top of the
        // message's own first line. The clearance used to sit on
        // `.MuiAlertTitle-root` instead, which no caller in the repo pairs with
        // an icon - so in practice every icon-passing alert overlapped its text.
        // Padding, not margin: with `width: '100%'` a left margin would overflow
        // the flex row and squeeze the `action` slot. AlertTitle renders inside
        // this element, so it inherits the offset and must not be indented again.
        message: ({ ownerState }) => ({
          width: '100%',
          ...(ownerState.icon ? { paddingLeft: 40 } : {}),
        }),
        icon: { position: 'absolute' },
      },
    },
  },
};

export const lightTheme: PaletteOptions = {
  mode: 'light',
  primary: { main: '#537f68' },
  secondary: { main: '#536A7F' },
  tertiary: { main: '#AABEBF' },
  error: { main: '#c74b4b' },
  // Harvest amber — a cleaner, more confident orange than the old pale
  // goldenrod (#E1B16A). Used for "in-progress / now" affordances across the
  // buyer flows (order status band, How-it-works step, cut-sheet warnings).
  warning: { main: '#D9862B', light: '#E8B063', dark: '#A9661C' },
  // Deepened from the old #A0C4FF, which was a dark-mode blue doing duty on a
  // near-white surface: as text or an outline it sat around 1.7:1 and read as
  // disabled. This clears 4.5:1 on `background.paper` and takes white contrast
  // text, so info buttons/chips are legible without extra per-component sx.
  // The dark palette keeps the pale blue — see below.
  info: { main: '#3D6FD1', light: '#A0C4FF', dark: '#2F63C4' },
  success: { main: '#006f35ff' },
  text: {
    primary: '#2A2A2A',
    secondary: 'rgba(42, 42, 42, 0.8)',
    disabled: 'rgba(42, 42, 42, 0.6)',
  },
  backdrop: { overlay: 'rgb(106, 142, 127)' },
  background: { default: '#f6f2ee', paper: '#fcf7f8' },
  statusRequested: { main: '#B57A5A' },
  statusScheduled: { main: '#B5635F' },
  statusInProgress: { main: '#C49A4F' },
  statusCompleted: { main: '#5E7A5B' },
  jobs: { main: '#A8754A' },
  invoices: { main: '#6E8C6B' },
  animalHeads: { main: '#8B6F4E' },
  users: { main: '#6E869B' },
  waitlist: { main: '#8B7596' },
  ...calendarColors,
};

export const darkTheme: PaletteOptions = {
  mode: 'dark',
  primary: {
    // main: '#537f68',
    main: Color('#537f68').lighten(0.2).hex(),
  },
  secondary: { main: Color('#536A7F').lighten(0.2).hex() },
  tertiary: { main: Color('#AABEBF').lighten(0.2).hex() },
  error: { main: '#c74b4b' },
  // Harvest amber, brightened slightly for the dark surface (see lightTheme).
  warning: { main: '#E39B3D', light: '#EDBB78', dark: '#C98428' },
  // Stays pale here: on the dark surface the deepened light-mode blue would
  // drop to ~2.6:1. This one sits at ~7:1. `dark` is a TEXT/outline token in
  // dark mode (4.5:1 on `background.paper`, up from the 3.6:1 MUI derived) —
  // NOT a surface: `MuiAlert.filledInfo` derives its own background instead.
  info: { main: '#A0C4FF', light: '#C3D9FF', dark: '#6E9AE8' },
  success: { main: '#006f35ff' },
  text: {
    primary: '#E0E0E0',
    secondary: 'rgba(240, 240, 240, 0.9)',
    disabled: 'rgba(240, 240, 240, 0.7)',
  },
  backdrop: { overlay: 'rgba(0, 0, 0, 0.5)' },
  background: { default: '#3E3E3E', paper: '#333' },
  statusRequested: { main: Color('#B57A5A').lighten(0.2).hex() },
  statusScheduled: { main: Color('#B5635F').lighten(0.2).hex() },
  statusInProgress: { main: Color('#C49A4F').lighten(0.2).hex() },
  statusCompleted: { main: Color('#5E7A5B').lighten(0.2).hex() },
  jobs: { main: Color('#A8754A').lighten(0.2).hex() },
  invoices: { main: Color('#6E8C6B').lighten(0.2).hex() },
  animalHeads: { main: Color('#8B6F4E').lighten(0.2).hex() },
  users: { main: Color('#6E869B').lighten(0.2).hex() },
  waitlist: { main: Color('#8B7596').lighten(0.2).hex() },
  ...calendarColors,
};

/**
 * Builds the app theme. Exported so the component overrides in `themeDefaults`
 * can be asserted directly (see `theme.spec.tsx`) rather than only through the
 * provider, which also pulls in CssBaseline and the date-adapter.
 * @param mode - Light or dark palette.
 * @param sidebarWidth - Added to every breakpoint so widths are content-relative.
 * @param phoneLandscape - Lift `sm` and `md` above the viewport so a phone on
 *   its side stays on the phone layout.
 * @returns The MUI theme.
 */
export const generateTheme = (
  mode: 'light' | 'dark',
  sidebarWidth: number,
  phoneLandscape = false,
): Theme => {
  const palette: PaletteOptions = mode === 'light' ? lightTheme : darkTheme;

  return createTheme({
    ...themeDefaults,
    palette,
    breakpoints: {
      values: {
        xs: 0,
        sm: phoneLandscape
          ? PHONE_LANDSCAPE_BREAKPOINTS.sm
          : 600 + sidebarWidth,
        md: phoneLandscape
          ? PHONE_LANDSCAPE_BREAKPOINTS.md
          : 900 + sidebarWidth,
        lg: 1200 + sidebarWidth,
        xl: 1546 + sidebarWidth,
      },
    },
  });
};

function CreateMuiSwitchableThemeProvider() {
  const Context = createContext({
    setMuiTheme: (_theme: 'light' | 'dark') => {},
    navMode: 'left',
    setNavMode: (_mode: 'top' | 'left') => {},
    navCollapsed: false,
    setNavCollapsed: (_collapsed: boolean) => {},
    themeMode: 'light',
    marginLeft: 0,
    marginTop: 0,
    // A CSS width, not a pixel count — '100%' for the full-bleed mobile drawer,
    // a number for the fixed desktop rail.
    navWidth: 0 as number | string,
    isTablet: false,
    isMobile: false,
    isPhoneLandscape: false,
    getUsableHeight: (vh: number = 100, offset: number = 0) =>
      `calc(${vh}dvh - ${offset}px)`,
  });

  const { Provider } = Context;

  const MuiSwitchableThemeProvider = ({
    children,
    forceTheme = undefined,
  }: {
    children: ReactNode;
    forceTheme?: 'light' | 'dark'; // This is a way for us to force a theme, useful for Storybook
  }) => {
    const localNavMode =
      typeof window !== 'undefined' && localStorage.getItem('navMode') === 'top'
        ? 'top'
        : 'left';
    const localTheme =
      typeof window !== 'undefined' &&
      localStorage.getItem('themeMode') === 'dark'
        ? 'dark'
        : 'light';
    const [themeMode, setThemeMode] = useState<'light' | 'dark'>(
      forceTheme ?? localTheme,
    );

    const [navMode, setNavModeInternal] = useState<'top' | 'left'>(
      localNavMode,
    );
    const [navCollapsed, setNavCollapsed] = useState(navMode === 'top');

    // A phone on its side is still a phone: it counts as mobile (and so as
    // tablet-or-smaller) however wide it is. `noSsr` so the first render
    // already knows, and the first theme is the phone one.
    const isPhoneLandscape = useMediaQuery(PHONE_LANDSCAPE_QUERY, {
      noSsr: true,
    });
    const isNarrowTablet = useMediaQuery('(max-width: 900px)');
    const isNarrowMobile = useMediaQuery('(max-width: 600px)');
    const isTablet = isNarrowTablet || isPhoneLandscape;
    const isMobile = isNarrowMobile || isPhoneLandscape;

    const setNavMode = (mode: 'top' | 'left', saveToLocalStorage = true) => {
      setNavModeInternal(mode);
      if (saveToLocalStorage && typeof window !== 'undefined') {
        localStorage.setItem('navMode', mode);
      }
    };

    useEffect(() => {
      if (forceTheme) {
        setThemeMode(forceTheme);
      }
    }, [forceTheme]);

    useEffect(() => {
      if (isTablet) {
        setNavMode('top', false);
        setNavCollapsed(true);
        return;
      }

      setNavMode(localNavMode, false);
    }, [isTablet, localNavMode]);

    useEffect(() => {
      document
        .getElementById('navbar')
        ?.setAttribute('data-bs-theme', themeMode);
      document.documentElement.setAttribute('data-bs-theme', themeMode);
      if (typeof window !== 'undefined') {
        localStorage.setItem('themeMode', themeMode);
      }
    }, [themeMode]);

    const marginLeft = useMemo(() => {
      if (navMode === 'top') return 0;

      return navCollapsed ? 64 : 240;
    }, [navMode, navCollapsed]);

    // Shorter on a phone in landscape, where the screen is ~400px tall and a
    // full-size bar is a sixth of it.
    const marginTop = useMemo(() => {
      if (navMode !== 'top') return 0;
      return isPhoneLandscape ? APP_HEADER_HEIGHT_COMPACT : APP_HEADER_HEIGHT;
    }, [navMode, isPhoneLandscape]);

    // Published so CSS can dodge the app header without knowing the nav mode —
    // the backdrop uses it to start below the header rather than dimming it.
    // Safari tints the status bar from the topmost element's own background, so
    // a dimmed AppBar leaves the strip above it bright and mismatched. 0 when
    // the header isn't a top bar (desktop left nav), making the dodge a no-op.
    useEffect(() => {
      document.documentElement.style.setProperty(
        '--app-header-height',
        `${marginTop}px`,
      );
    }, [marginTop]);

    const navWidth = useMemo(() => {
      // Full width on mobile — '100%' rather than a pixel snapshot of
      // window.innerWidth: the drawer paper is fixed, so 100% resolves against
      // the viewport every frame. innerWidth also counts the scrollbar (layout
      // doesn't), and a memoised snapshot goes stale on resize or rotate,
      // overflowing the screen sideways.
      if (isMobile) return navCollapsed ? 0 : '100%';

      if (navMode === 'top') return navCollapsed ? 0 : 240;
      return navCollapsed ? 64 : 240;
    }, [navMode, navCollapsed, isMobile]);

    const getUsableHeight = useCallback(
      // `dvh`, not `vh`. On a phone `vh` is the height with the browser's
      // toolbar tucked away, so a "full height" list ran on behind Safari's
      // bottom bar and its pagination footer could not be reached. `dvh` is
      // the height actually visible; on desktop the two are the same.
      (vh: number = 100, offset: number = 0) =>
        `calc(${vh}dvh - ${marginTop + offset}px)`,
      [marginTop],
    );

    // Derived, not kept in state and rebuilt in an effect: that left one
    // commit on the old breakpoints whenever an input changed, so rotating a
    // phone rendered a frame of desktop layout on the way to landscape.
    const theme = useMemo(
      () => generateTheme(themeMode, marginLeft, isPhoneLandscape),
      [themeMode, marginLeft, isPhoneLandscape],
    );

    // The status bar tint when nothing is overlaying — the app header's own
    // colour, so the strip above the header reads as part of it.
    useEffect(() => {
      setThemeColorBase(
        theme.palette.mode === 'dark'
          ? theme.palette.primary.dark
          : theme.palette.primary.main,
      );
    }, [theme]);

    return (
      <Provider
        value={{
          setMuiTheme: setThemeMode,
          themeMode,
          navMode,
          setNavMode,
          navCollapsed,
          setNavCollapsed,
          marginLeft,
          marginTop,
          navWidth,
          isTablet,
          isMobile,
          isPhoneLandscape,
          getUsableHeight,
        }}
      >
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <LocalizationProvider dateAdapter={AdapterMoment}>
            {children}
          </LocalizationProvider>
        </ThemeProvider>
      </Provider>
    );
  };

  const useMuiSwitchableTheme = () => {
    const context = useContext(Context);

    if (!context) {
      throw new Error(
        'useMuiSwitchableTheme must be used within a MuiSwitchableThemeProvider',
      );
    }

    return context;
  };

  return { MuiSwitchableThemeProvider, useMuiSwitchableTheme };
}

const { MuiSwitchableThemeProvider, useMuiSwitchableTheme } =
  CreateMuiSwitchableThemeProvider();

export { MuiSwitchableThemeProvider, useMuiSwitchableTheme };
